import React, { useState } from 'react';
import {
  ActionIcon, Alert, Badge, Box, Button, Card, Collapse, Group, Stack, Text, Textarea, Title,
} from '@mantine/core';
import { IconAlertCircle, IconChevronDown, IconChevronUp, IconLock } from '@tabler/icons-react';

import { answerOpenQuestion } from '../api_scripts/solnStudioClient';

// Open-question severity (§3.7 taxonomy: blocker / important / minor) → badge color. Advisory only —
// Build's gate stays soft (§4.5) — but a blocker is a question whose answer the Build genuinely needs.
export const OQ_SEVERITY_COLOR = { blocker: 'red', important: 'orange', minor: 'gray' };

// Count of unresolved BLOCKER-severity open questions — surfaced in the section header + the Build
// approval modal (still advisory).
export function blockerCount(openQuestions = []) {
  return openQuestions.filter((q) => q.status !== 'resolved' && q.severity === 'blocker').length;
}

// The collapsible Open Questions section at the bottom of the Design tab. Answering a question
// resolves it into a locked decision the next refine honors; the Customer Worksheet document is the
// bulk/offline path for the same questions. Answering does a BACKGROUND refresh (P0.3) — it must
// never bounce the whole workspace through a full-page spinner.
export default function OpenQuestionsSection({ project, designModel, getApiToken, onChanged, locked }) {
  const [open, setOpen] = useState(true);
  const oqs = designModel?.openQuestions || [];
  const openCount = oqs.filter((q) => q.status !== 'resolved').length;
  const blockers = blockerCount(oqs);

  return (
    <Card withBorder padding="lg" radius="md">
      <Group justify="space-between" wrap="nowrap" style={{ cursor: 'pointer' }} onClick={() => setOpen((o) => !o)}>
        <Group gap="xs">
          <Title order={4}>Open questions</Title>
          <Badge variant="light" color={openCount > 0 ? 'yellow' : 'green'}>{openCount} open</Badge>
          {blockers > 0 && <Badge variant="light" color="red">{blockers} blocker(s)</Badge>}
          <Badge variant="light" color="gray">{oqs.length - openCount} resolved</Badge>
        </Group>
        {/* The real toggle control: a focusable button so keyboard users can expand/collapse (the
            surrounding Group's onClick is a mouse-only convenience). aria-expanded exposes state to SRs. */}
        <ActionIcon
          variant="subtle" color="gray"
          aria-label={open ? 'Collapse open questions' : 'Expand open questions'}
          aria-expanded={open}
          onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        >
          {open ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
        </ActionIcon>
      </Group>
      <Collapse in={open}>
        <Box mt="md">
          <OpenQuestionsList
            project={project}
            designModel={designModel}
            getApiToken={getApiToken}
            onChanged={onChanged}
            locked={locked}
          />
        </Box>
      </Collapse>
    </Card>
  );
}

function OpenQuestionsList({ project, designModel, getApiToken, onChanged, locked }) {
  const oqs = designModel?.openQuestions || [];
  if (oqs.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No open questions — anything inferred without a verified citation would appear here. Generate or
        Refine the design to surface gaps.
      </Text>
    );
  }
  return (
    <Stack>
      <Text size="sm" c="dimmed">
        Answer a question here to resolve it (it becomes a locked decision the next Refine honors and never
        re-asks), or send them to the customer via the <b>Customer Worksheet</b> document above and upload
        the filled copy to resolve them in bulk.
      </Text>
      {locked && (
        <Alert color="blue" icon={<IconLock size={16} />}>
          A generation job is running — answering is paused until it finishes.
        </Alert>
      )}
      {oqs.map((q) => (
        <OpenQuestionCard
          key={q.id}
          project={project}
          question={q}
          getApiToken={getApiToken}
          onChanged={onChanged}
          locked={locked}
        />
      ))}
    </Stack>
  );
}

function OpenQuestionCard({ project, question: q, getApiToken, onChanged, locked }) {
  const resolved = q.status === 'resolved';
  const [answer, setAnswer] = useState(q.answer || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const act = async (status) => {
    setBusy(true);
    setError(null);
    try {
      await answerOpenQuestion(
        project.projectId,
        q.id,
        { expectedVersion: project.version, answer, status },
        getApiToken,
      );
      onChanged();
    } catch (err) {
      setError(err.reason === 'locked'
        ? 'A generation job is running — answering is paused until it finishes.'
        : err.reason === 'stale-version'
        ? 'This project changed — reopen it and retry.'
        : err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card withBorder padding="md" radius="md">
      <Group justify="space-between" mb="xs" wrap="nowrap" align="flex-start">
        <Text fw={500}>#{q.num} {q.question}</Text>
        <Group gap="xs" wrap="nowrap">
          {q.severity && !resolved && (
            <Badge variant="light" color={OQ_SEVERITY_COLOR[q.severity] || 'gray'}>{q.severity}</Badge>
          )}
          <Badge variant="light" color={resolved ? 'green' : 'yellow'}>{q.status || 'open'}</Badge>
        </Group>
      </Group>
      {(q.category || q.origin) && (
        <Text size="xs" c="dimmed" mb="xs">
          {q.category}{q.origin ? ` · from ${q.origin}` : ''}
        </Text>
      )}
      <Textarea
        placeholder="Answer this question (e.g. the customer's confirmed decision)…"
        autosize
        minRows={2}
        value={answer}
        onChange={(e) => setAnswer(e.currentTarget.value)}
        disabled={busy || locked}
      />
      {error && <Alert color="red" icon={<IconAlertCircle size={16} />} mt="xs">{error}</Alert>}
      <Group justify="flex-end" mt="xs" gap="xs">
        {resolved && (
          <Button variant="subtle" color="gray" size="xs" loading={busy} disabled={locked} onClick={() => act('open')}>
            Reopen
          </Button>
        )}
        <Button
          color="orange"
          size="xs"
          loading={busy}
          disabled={locked || !answer.trim()}
          onClick={() => act('resolved')}
        >
          {resolved ? 'Update answer' : 'Resolve'}
        </Button>
      </Group>
    </Card>
  );
}
